import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge } from '../atoms';
import {
  Notification as AppNotification,
  isUnread,
  metaFor,
  routeFor,
  relativeTime,
  urgencyVariant,
  urgencyLabel,
} from '../lib/notifications';
import {
  StatCard, StatCardRow,
  DataTable,
  ActivityPanel,
  ActivityEvent,
  DonutChart,
  DonutSegment,
  Tabs,
  NotificationTypeIcon,
} from '../organisms';
import { useIsCompact } from '../lib/useMediaQuery';
import {
  useActivityStream,
  mergeActivityEvents,
  ActivityStreamEvent,
} from '../lib/useActivityStream';
import { formatDate, formatDateTime } from '../lib/orgTimezone';
import { activityTitle } from '../lib/activityFeed';
import { eventTitle, metaFor as eventMetaFor, type CalendarEvent } from './CalendarShared';
import {
  pendingBoardOrderColumns,
  hearingNoticeColumns,
  PendingBoardOrderRow,
  HearingNoticeRow,
} from './AppealsQueues';

/**
 * Dashboard — the post-login landing page.
 *
 * Role-dispatched off GET /api/dashboard: admin (operational rollup),
 * agent (own book / portfolio), analyst (review queue). All three consume
 * the payloads src/services/dashboard.js already returns — no v2 API change.
 *
 * Phase 11 Block 7 PR 1 (admin) + Domain 01.2 (agent + analyst, responsive).
 */

interface DashboardParcel {
  id: string;
  account_number: string;
  name: string | null;
  owner_name: string | null;
  client_name: string | null;
  property_name: string | null;
  assessor_name: string | null;
  current_market_value: number | null;
  proposed_market_value: number | null;
  prior_market_value: number | null;
  under_appeal: boolean;
  settled: boolean;
  status: string;
}

interface AdminDashboardResponse {
  dashboardType: 'admin';
  stats: {
    clients: number;
    properties: number;
    parcels: number;
    setupQueue: number;
    setupClientsCount?: number;
    setupPropertiesCount?: number;
    pendingBills: number;
    appealsInProgress: number;
    pendingInvoiceItems: number;
    // MONEY — the service omits both for the money-blind `administrator` role, so
    // they are optional and every read must tolerate their absence.
    totalBillAmount?: number;
    pendingInvoiceAmount?: number;
  };
  // Per-user launchpad KPIs, scoped to the requesting user's own book. Optional
  // so an older payload (or a money-blind role that omits `pendingInvoicing`)
  // renders gracefully with fallbacks. `pendingInvoicing` is a MONEY figure and
  // is absent for money-blind roles.
  myStats?: {
    myProperties: number;
    myAppeals: number;
    myPendingBills: number;
    myPendingInvoiceItems?: number;
    pendingInvoicing?: number;   // MONEY — absent for money-blind roles
  };
  pendingBills: Array<{
    id: string;
    parcel_name: string | null;
    parcel_id: string | null;
    property_name: string;
    collector_name: string;
    due_date: string | null;
    tax_amount: number | null;
    status: string;
  }>;
  appealPipeline: {
    notFiled: number;
    underAppeal: number;
    settled: number;
  };
  billsByStatus: Array<{ status: string; count: number }>;
  recentAudit: Array<{
    id: string;
    entity_type: string;
    entity_id: string;
    field_name?: string;
    action: 'create' | 'update' | 'delete' | 'import_accept' | 'import_reject' | 'bulk_update' | 'year_roll';
    actor: string | null;
    created_at: string;
    // Activity layer (migration 102): the follow/assigned-scoped activity events
    // overlaid on the org-wide audit echo carry a pre-humanized summary + from→to.
    summary?: string;
    from_state?: string | null;
    to_state?: string | null;
    event_type?: string;
  }>;
}

interface AgentDashboardResponse {
  dashboardType: 'agent';
  stats: {
    myParcels: number;
    totalPortfolioValue: number;
    underAppeal: number;
    settled: number;
    daysToDeadline: number | null;
  };
  myParcels: DashboardParcel[];
  deadlines: Array<{
    id: string;
    assessor_name: string | null;
    re_appeal_deadline: string | null;
    pp_appeal_deadline: string | null;
  }>;
  valueComparison: Array<{ name: string; prior: number; proposed: number }>;
  statusBreakdown: Record<string, number>;
}

interface AnalystDashboardResponse {
  dashboardType: 'analyst';
  stats: {
    assignedParcels: number;
    valuesNeedingReview: number;
    recentDocs: number;
  };
  assignedParcels: DashboardParcel[];
  valuesNeedingReview: Array<{
    id: string;
    parcel_name: string | null;
    tax_year: number;
    value_type: string;
    market_total: number | null;
    assessed_total: number | null;
    status: string;
  }>;
  recentDocs: Array<{
    id: string;
    name: string | null;
    document_type: string | null;
    filename: string | null;
    year: number | null;
    created_at: string;
    status: string | null;
  }>;
  valueBars: Array<{ name: string; prior: number; proposed: number }>;
}

/**
 * Consultant dashboard — the external, book-scoped role (consultant-role /
 * migration 029). The BE (services/dashboard.js getConsultantDashboard) returns
 * the SAME portfolio shape as the agent payload, scoped to the parcels on which
 * this user is the named consultant, so it renders through the shared portfolio
 * view below with a consultant-appropriate header.
 */
interface ConsultantDashboardResponse extends Omit<AgentDashboardResponse, 'dashboardType'> {
  dashboardType: 'consultant';
}

/** Both book-scoped portfolio payloads share every field but the discriminant. */
type BookDashboardResponse = AgentDashboardResponse | ConsultantDashboardResponse;

type DashboardResponse =
  | AdminDashboardResponse
  | AgentDashboardResponse
  | AnalystDashboardResponse
  | ConsultantDashboardResponse;

const money = (v: number | null | undefined) =>
  v != null ? `$${Math.round(v).toLocaleString()}` : '—';

/**
 * Make a KPI card behave as a keyboard-operable link into a filtered list. Shared by
 * all four role views so a card's click target is wired one way everywhere.
 *
 * The rule these links follow: a card whose headline value is a COUNT links to the
 * list whose total equals that count (asserted in
 * tests/integration/dashboard-card-parity.test.js). A card whose headline is a MONEY
 * sum gets no link — there is no list whose row count it could be checked against —
 * and purely navigational cards (a deadline countdown → the calendar) are linked but
 * carry no count to match.
 */
const makeCardLink = (navigate: (to: string) => void) => (to: string) => ({
  onClick: () => navigate(to),
  role: 'button' as const,
  tabIndex: 0,
  onKeyDown: (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(to); }
  },
  style: { cursor: 'pointer' },
});

export function Dashboard() {
  const compact = useIsCompact();
  const query = useQuery<DashboardResponse>({
    queryKey: ['dashboard'],
    queryFn: ({ signal }) => api.get<DashboardResponse>('/api/dashboard', signal),
    // The activity panel is now driven LIVE by the /api/activity/stream SSE
    // consumer below (Domain 02.2), so this poll no longer carries the feed —
    // it only refreshes the aggregate stats + pending-bills tables at a relaxed
    // cadence. The live events prepend on top of the last-loaded snapshot.
    refetchInterval: 120_000,
  });

  // Live activity events pushed since mount (admin dashboard only shows the
  // org-wide feed). Newest-first; deduped/capped against the snapshot at render.
  const [liveActivity, setLiveActivity] = useState<ActivityStreamEvent[]>([]);
  const handleStreamEvent = useCallback((ev: ActivityStreamEvent) => {
    setLiveActivity(prev => (prev.some(e => e.id === ev.id) ? prev : [ev, ...prev].slice(0, 12)));
  }, []);
  useActivityStream({
    enabled: query.data?.dashboardType === 'admin',
    onEvent: handleStreamEvent,
  });

  if (query.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading dashboard…</p>;
  }
  if (query.error) {
    return <ErrorView message={(query.error as Error).message} />;
  }
  if (!query.data) return null;

  if (query.data.dashboardType === 'agent' || query.data.dashboardType === 'consultant') {
    return <BookDashboard data={query.data} compact={compact} />;
  }
  if (query.data.dashboardType === 'analyst') return <AnalystDashboard data={query.data} compact={compact} />;

  const data = query.data;
  return <AdminDashboard data={data} compact={compact} liveActivity={liveActivity} />;
}

/* ── Admin dashboard — the appeal-centric, per-user launchpad ──────────── */

function AdminDashboard({
  data, compact, liveActivity,
}: {
  data: AdminDashboardResponse;
  compact: boolean;
  liveActivity: ActivityStreamEvent[];
}) {
  const navigate = useNavigate();

  const totalAppeals = data.appealPipeline.notFiled + data.appealPipeline.underAppeal + data.appealPipeline.settled;

  // Per-user launchpad KPIs. Fall back gracefully when a field is absent (older
  // payload) or redacted (`pendingInvoicing` omitted for money-blind roles).
  const my = data.myStats;

  // This dashboard is served ONLY to firm-wide roles (routes/dashboard.js dispatches
  // super_admin/admin/administrator here), and a firm-wide role has no row scope of
  // its own — so `myStats` is an EMPTY personal book unless someone also assigned that
  // person parcels individually. Rendering it unconditionally is what made the cards
  // read 0 while the org held 95 parcels / 40 pending bills. So: always show the
  // firm-wide row (org-wide figures, org-wide links), and show the personal row only
  // when the caller genuinely has a book — never as a row of misleading zeros.
  const hasOwnBook = !!my && (my.myProperties > 0 || my.myAppeals > 0 || my.myPendingBills > 0);

  // Appeal pipeline — now the PRIMARY chart (the "Tax bill status" donut is
  // removed; tax bills are no longer the headline). Each segment is clickable →
  // the Parcels list filtered to that state (index-aligned with appealSegmentLinks
  // below): Under appeal → under_appeal=true; Settled → settled=true; Not filed →
  // the rest (neither flag). ParcelsList reads these params on mount.
  const appealSegments: DonutSegment[] = [
    { label: 'Not filed', value: data.appealPipeline.notFiled, color: 'var(--color-status-gray)' },
    { label: 'Under appeal', value: data.appealPipeline.underAppeal, color: 'var(--color-status-amber)' },
    { label: 'Settled', value: data.appealPipeline.settled, color: 'var(--brand-primary)' },
  ];
  const appealSegmentLinks = [
    '/parcels?under_appeal=false&settled=false',
    '/parcels?under_appeal=true',
    '/parcels?settled=true',
  ];

  const snapshotEvents: ActivityEvent[] = data.recentAudit.map(ev => ({
    id: ev.id,
    kind: ev.action === 'delete' ? 'danger' : ev.action === 'create' ? 'success' : 'info',
    // Activity-layer rows (migration 102) carry a pre-humanized summary with the
    // from→to transition; the org-wide audit echo falls back to the social phrasing.
    title: ev.summary ? ev.summary : activityTitle(ev.entity_type, ev.action, ev.actor),
    description: '',
    timestamp: formatDateTime(ev.created_at),
  }));
  // Prepend the live-streamed events, dedupe by id (a reconnect replay may
  // re-deliver a snapshot row), and cap the panel at 10 rows. Recent activity is
  // now scoped per-user (things I follow or am assigned to) via the follows table
  // + assignment book, overlaid on the org-wide audit trail (resolves #42).
  const liveMapped: ActivityEvent[] = liveActivity.map(streamToActivityEvent);
  const activityEvents = mergeActivityEvents(liveMapped, snapshotEvents, 10);

  const cardLink = makeCardLink(navigate);

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`Your launchpad · ${data.stats.clients} clients · ${data.stats.properties} properties · ${data.stats.parcels} parcels`}
      />

      {/* Firm-wide KPI cards. Each card's VALUE is the count of rows its own link
          returns — the card query and the list query apply the same predicate — so
          clicking a tile always lands on exactly the set the number described:
            Parcels under appeal → /parcels?under_appeal=true
            Pending tax bills    → /tax-bills?status=pending   (the no_activity/received/processed set)
            Billable items       → /invoice-items?status=pending
            Properties in setup  → /properties?status=in_setup
          `tests/integration/dashboard-card-parity.test.js` asserts each equality. */}
      <div data-tour="dashboard-kpis" style={{ marginBottom: 'var(--sp-7)' }}>
        <StatCardRow>
          <StatCard {...cardLink('/parcels?under_appeal=true')} label="Parcels under appeal" value={String(data.appealPipeline.underAppeal)} caption="across the firm" accent="amber" />
          <StatCard {...cardLink('/tax-bills?status=pending')} label="Pending tax bills" value={String(data.stats.pendingBills)} caption="awaiting activity" accent="info" />
          <StatCard
            {...cardLink('/invoice-items?status=pending')}
            label="Billable items"
            value={String(data.stats.pendingInvoiceItems)}
            // The money total rides as the CAPTION, not the headline value — a dollar
            // figure can't be checked against a row count. Omitted for money-blind
            // roles (the service drops pendingInvoiceAmount for them).
            caption={data.stats.pendingInvoiceAmount != null ? `${money(data.stats.pendingInvoiceAmount)} ready to invoice` : 'ready to invoice'}
            accent="green"
          />
          <StatCard {...cardLink('/properties?status=in_setup')} label="Properties in setup" value={String(data.stats.setupPropertiesCount ?? 0)} caption="awaiting setup" accent="hunter" />
        </StatCardRow>
      </div>

      {/* The caller's OWN book — rendered only when they actually hold one, so a
          firm-wide admin with no personal assignments never sees a row of zeros.
          Every link carries ?assigned=me, the same predicate the counts use. */}
      {hasOwnBook && my && (
        <div style={{ marginBottom: 'var(--sp-7)' }}>
          <h2 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>My book</h2>
          <StatCardRow>
            <StatCard {...cardLink('/properties?assigned=me')} label="My properties" value={String(my.myProperties)} caption="properties in your book" accent="hunter" />
            <StatCard {...cardLink('/parcels?under_appeal=true&assigned=me')} label="My appeals" value={String(my.myAppeals)} caption="open appeals you own" accent="amber" />
            <StatCard {...cardLink('/tax-bills?status=pending&assigned=me')} label="My pending bills" value={String(my.myPendingBills)} caption="awaiting activity" accent="info" />
            {my.myPendingInvoiceItems != null && (
              <StatCard
                {...cardLink('/invoice-items?status=pending&assigned=me')}
                label="My billable items"
                value={String(my.myPendingInvoiceItems)}
                // Count is the headline (checkable against the list); the money sum is
                // the caption and disappears for money-blind roles.
                caption={my.pendingInvoicing != null ? `${money(my.pendingInvoicing)} in tax savings to invoice` : 'awaiting invoice'}
                accent="green"
              />
            )}
          </StatCardRow>
        </div>
      )}

      {/* Primary chart = Appeal pipeline (tax-bill status donut removed —
          intentional demotion), beside an upcoming-hearings mini-list. */}
      <ChartRow compact={compact}>
        <DonutChart
          title="Appeal pipeline"
          caption="Parcels by appeal state"
          segments={appealSegments}
          centerLabel="Parcels"
          countPill={`${totalAppeals} parcels`}
          emptyMessage="No parcels yet."
          onSegmentClick={(_seg, i) => navigate(appealSegmentLinks[i])}
        />
        <UpcomingEventsCard onOpenCalendar={() => navigate('/calendar')} />
      </ChartRow>

      {/* Top notifications for the current user (feature 41) — the dashboard slice
          of the same self-scoped feed the header bell + Notifications page read. */}
      <DashboardTasks />
      <DashboardNotifications />


      {/* Appeals Work List — the two most-worked queues, on the dashboard. */}
      <section style={{ marginBottom: 'var(--sp-7)' }}>
        <AppealsWorklist
          onOpenAppeal={(id) => navigate(`/appeals/${id}`)}
          onViewAll={() => navigate('/appeals/queues')}
        />
      </section>

      <section>
        <ActivityPanel title="Recent activity · live" events={activityEvents} emptyMessage="No recent activity logged." />
      </section>
    </>
  );
}

/* ── Appeals Work List (on the dashboard) — two tabs, top 10 each ──────── */

const WORKLIST_TABS = [
  { value: 'pending-board-orders', label: 'Pending Board Orders' },
  { value: 'hearing-notices', label: 'Hearing Notices' },
];
const WORKLIST_CAP = 10;

function AppealsWorklist({
  onOpenAppeal, onViewAll,
}: {
  onOpenAppeal: (id: string) => void;
  onViewAll: () => void;
}) {
  const [tab, setTab] = useState('pending-board-orders');

  const boardOrders = useQuery<PendingBoardOrderRow[]>({
    queryKey: ['appeals-queue', 'pending-board-orders'],
    queryFn: ({ signal }) => api.get<PendingBoardOrderRow[]>('/api/workflow/appeals/queues/pending-board-orders', signal),
  });
  const hearings = useQuery<HearingNoticeRow[]>({
    queryKey: ['appeals-queue', 'hearing-notices'],
    queryFn: ({ signal }) => api.get<HearingNoticeRow[]>('/api/workflow/appeals/queues/hearing-notices', signal),
  });

  const active = tab === 'pending-board-orders' ? boardOrders : hearings;
  const boardRows = (boardOrders.data ?? []).slice(0, WORKLIST_CAP);
  const hearingRows = (hearings.data ?? []).slice(0, WORKLIST_CAP);
  const counts: Record<string, number | undefined> = {
    'pending-board-orders': boardOrders.data?.length,
    'hearing-notices': hearings.data?.length,
  };

  return (
    <>
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--sp-3)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-xl)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Appeals work list</h2>
        <button
          type="button"
          onClick={onViewAll}
          style={{
            border: 'none', background: 'none', padding: 0, cursor: 'pointer',
            color: 'var(--brand-primary)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
          }}
        >View all →</button>
      </header>

      <Tabs
        items={WORKLIST_TABS.map(t => ({ value: t.value, label: t.label, count: counts[t.value] }))}
        activeValue={tab}
        onChange={setTab}
      />

      {active.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : active.error ? (
        <EmptyView message={`Failed to load this queue: ${(active.error as Error).message}`} />
      ) : tab === 'pending-board-orders' ? (
        <DataTable data={boardRows} columns={pendingBoardOrderColumns} onRowClick={(r) => onOpenAppeal(r.id)} emptyMessage="No appeals are awaiting a board order." />
      ) : (
        <DataTable data={hearingRows} columns={hearingNoticeColumns} onRowClick={(r) => onOpenAppeal(r.id)} emptyMessage="No appeals have a hearing notice awaiting scheduling." />
      )}
    </>
  );
}

/* ── Upcoming events mini-list — the user's next calendar events (all types),
      the SAME data + phrasing the calendar's own Upcoming list shows. Click
      through opens the calendar, where those same events sit at the top. ──── */

function UpcomingEventsCard({ onOpenCalendar }: { onOpenCalendar: () => void }) {
  const navigate = useNavigate();
  // /api/calendar/upcoming (book-scoped, next 30 days) — the same source the
  // calendar's Upcoming panel derives from, so the dashboard list matches the
  // page the click lands on.
  const upcoming = useQuery<{ events: CalendarEvent[] }>({
    queryKey: ['calendar-upcoming', 'dashboard'],
    queryFn: ({ signal }) => api.get<{ events: CalendarEvent[] }>('/api/calendar/upcoming?days=30', signal),
  });
  const rows = (upcoming.data?.events ?? []).slice(0, 5);

  return (
    <div
      data-tour="upcoming-events"
      role="button"
      tabIndex={0}
      aria-label="Upcoming events — open the calendar"
      onClick={onOpenCalendar}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenCalendar(); } }}
      style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-5)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Upcoming events</h3>
        <span style={{ color: 'var(--brand-primary)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>Calendar →</span>
      </header>
      {upcoming.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)', margin: 0 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--color-muted)', margin: 0, fontSize: 'var(--fs-sm)' }}>Nothing on the calendar in the next 30 days.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {rows.map((e) => {
            // Guard a DB event_type not in TYPE_META (dropped-CHECK future-proofing).
            const meta = eventMetaFor(e.event_type); // falls back to a neutral 'Event' for a type not in TYPE_META
            return (
              <li key={e.id}>
                {/* Matches the calendar page's Upcoming list item: bordered, left
                    event-type accent, title + type badge, then the date line.
                    Clicking a row deep-links to the calendar with THIS event
                    surfaced (its month + detail modal); stopPropagation so it
                    doesn't also fire the card's open-calendar handler. */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${eventTitle(e)} on the calendar`}
                  onClick={(ev) => { ev.stopPropagation(); navigate(`/calendar?event=${e.id}&date=${e.event_date}`); }}
                  onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); navigate(`/calendar?event=${e.id}&date=${e.event_date}`); } }}
                  style={{
                  border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
                  borderLeft: `3px solid ${meta.accent}`, padding: 'var(--sp-2) var(--sp-3)',
                  cursor: 'pointer',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-2)' }}>
                    <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {eventTitle(e)}
                    </span>
                    <Badge variant={meta.variant}>{meta.label}</Badge>
                  </div>
                  <div style={{ marginTop: 2, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                    {formatDate(e.event_date)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ── Book dashboard — an agent's or consultant's own book ─────────────── */

function BookDashboard({ data, compact }: { data: BookDashboardResponse; compact: boolean }) {
  const navigate = useNavigate();
  const cardLink = makeCardLink(navigate);
  const columns = useMemo(() => parcelColumns(), []);
  const isConsultant = data.dashboardType === 'consultant';

  const deadlineEvents: ActivityEvent[] = data.deadlines.map(d => ({
    id: d.id,
    kind: 'info',
    title: <>{d.assessor_name || 'Assessor'}</>,
    description: 'RE appeal deadline',
    timestamp: formatDate(d.re_appeal_deadline),
  }));

  // Portfolio status donut — the V1 agent "Portfolio status" chart, wired to
  // the real statusBreakdown aggregate ({active, under_appeal, settled}).
  const sb = data.statusBreakdown || {};
  const portfolioSegments: DonutSegment[] = [
    { label: 'Not filed', value: sb.active || 0, color: 'var(--color-status-gray)' },
    { label: 'Under appeal', value: sb.under_appeal || 0, color: 'var(--color-status-amber)' },
    { label: 'Settled', value: sb.settled || 0, color: 'var(--brand-primary)' },
  ];
  const portfolioTotal = portfolioSegments.reduce((a, s) => a + s.value, 0);

  return (
    <>
      <PageHeader
        title={isConsultant ? 'My book' : 'My portfolio'}
        subtitle={`${data.stats.myParcels} parcels ${isConsultant ? 'you consult on' : 'in your book'}`}
      />

      {/* Count cards deep-link into the list whose total equals them; ?assigned=me
          resolves to THIS role's own book (an agent's assignment legs, a consultant's
          named-consultant parcels). Portfolio value carries no link — a money sum has
          no row count to be checked against. */}
      <div style={{ marginBottom: 'var(--sp-7)' }}>
        <StatCardRow>
          <StatCard {...cardLink('/parcels?assigned=me')} label="My parcels" value={String(data.stats.myParcels)} caption="assigned to you" accent="hunter" />
          <StatCard label="Portfolio value" value={money(data.stats.totalPortfolioValue)} caption="proposed / current" accent="info" />
          <StatCard {...cardLink('/parcels?under_appeal=true&assigned=me')} label="Under appeal" value={String(data.stats.underAppeal)} caption={`${data.stats.settled} settled`} accent="amber" />
          <StatCard
            {...cardLink('/calendar')}
            label="Next deadline"
            value={data.stats.daysToDeadline != null ? `${data.stats.daysToDeadline}d` : '—'}
            caption={data.stats.daysToDeadline != null ? 'days remaining' : 'no upcoming deadline'}
            accent={data.stats.daysToDeadline != null && data.stats.daysToDeadline <= 14 ? 'red' : 'green'}
          />
        </StatCardRow>
      </div>

      {/* 1.4: match the super-admin dashboard — donut LEFT + upcoming deadlines RIGHT
          in the same ChartRow 2-up grid, with My parcels full-width below. */}
      <ChartRow compact={compact}>
        <DonutChart
          title="Portfolio status"
          caption="Parcels by appeal state · current tax year"
          segments={portfolioSegments}
          centerLabel="Parcels"
          countPill={`${portfolioTotal} parcels`}
          emptyMessage="No parcels assigned yet."
        />
        <ActivityPanel title="Upcoming deadlines" events={deadlineEvents} emptyMessage="No upcoming deadlines." />
      </ChartRow>

      <section style={{ marginTop: 'var(--sp-7)' }}>
        <SectionHeader title="My parcels" caption={`${data.myParcels.length}`} />
        {data.myParcels.length === 0 ? (
          <EmptyView message="No parcels assigned to you yet." />
        ) : (
          <DataTable data={data.myParcels} columns={columns} onRowClick={(p) => navigate(`/parcels/${p.id}`)} />
        )}
      </section>
    </>
  );
}

/* ── Analyst dashboard — the review queue ─────────────────────────────── */

function AnalystDashboard({ data, compact }: { data: AnalystDashboardResponse; compact: boolean }) {
  const navigate = useNavigate();
  const cardLink = makeCardLink(navigate);
  const parcelCols = useMemo(() => parcelColumns(), []);

  const valueCols = useMemo<ColumnDef<AnalystDashboardResponse['valuesNeedingReview'][number]>[]>(() => [
    { accessorKey: 'parcel_name', header: 'Parcel', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'tax_year', header: 'Year' },
    { accessorKey: 'value_type', header: 'Type' },
    { accessorKey: 'market_total', header: 'Market', cell: ({ getValue }) => money(getValue() as number | null) },
    { accessorKey: 'assessed_total', header: 'Assessed', cell: ({ getValue }) => money(getValue() as number | null) },
  ], []);

  const docEvents: ActivityEvent[] = data.recentDocs.map(d => ({
    id: d.id,
    kind: 'info',
    title: <>{d.name || d.filename || 'Document'}</>,
    description: (d.document_type || 'document').replace(/_/g, ' '),
    timestamp: formatDateTime(d.created_at),
  }));

  return (
    <>
      <PageHeader title="My review queue" subtitle={`${data.stats.valuesNeedingReview} values awaiting review`} />

      <div style={{ marginBottom: 'var(--sp-7)' }}>
        <StatCardRow>
          {/* ?assigned=me resolves to the ANALYST's own book (parcels.assistant_id) —
              the shared personalScopeClause picks the predicate per role, so this
              equals the card. */}
          <StatCard {...cardLink('/parcels?assigned=me')} label="Assigned parcels" value={String(data.stats.assignedParcels)} caption="you assist on" accent="hunter" />
          {/* No link: there is no list surface for parcel_values in the received state
              (/parcel-values redirects to /parcels), and the review queue itself is
              rendered directly below with row-click into each value. */}
          <StatCard label="Values to review" value={String(data.stats.valuesNeedingReview)} caption="status: received" accent="amber" />
          {/* A real org-wide document count (the service no longer reports the length
              of the LIMIT-10 preview list below, which capped this card at 10) — so it
              equals the /documents list total. */}
          <StatCard {...cardLink('/documents')} label="Documents" value={String(data.stats.recentDocs)} caption="in the document library" accent="info" />
        </StatCardRow>
      </div>

      <TwoColumn compact={compact}>
        <section>
          <SectionHeader title="Values needing review" caption={`${data.valuesNeedingReview.length}`} />
          {data.valuesNeedingReview.length === 0 ? (
            <EmptyView message="Nothing awaiting review." />
          ) : (
            <DataTable data={data.valuesNeedingReview} columns={valueCols} onRowClick={(v) => navigate(`/parcel-values/${v.id}`)} />
          )}
        </section>

        <ActivityPanel title="Recent documents" events={docEvents} emptyMessage="No recent documents." />
      </TwoColumn>

      {data.assignedParcels.length > 0 && (
        <div style={{ marginTop: 'var(--sp-7)' }}>
          <SectionHeader title="Assigned parcels" caption={`${data.assignedParcels.length}`} />
          <DataTable data={data.assignedParcels} columns={parcelCols} onRowClick={(p) => navigate(`/parcels/${p.id}`)} />
        </div>
      )}
    </>
  );
}

/* ── Shared building blocks ───────────────────────────────────────────── */

/**
 * Map a live SSE activity event to the ActivityPanel row shape, matching the
 * snapshot mapping above so live and polled rows render identically.
 */
function streamToActivityEvent(ev: ActivityStreamEvent): ActivityEvent {
  return {
    id: ev.id,
    kind: ev.action === 'delete' ? 'danger' : ev.action === 'create' ? 'success' : 'info',
    // Activity-layer frames (migration 102) carry a rich humanized summary with the
    // lifecycle from→to; audit-derived frames keep the social-feed phrasing.
    title: ev.event_type ? ev.message : activityTitle(ev.entity_type, ev.action, ev.actor),
    description: '',
    timestamp: formatDateTime(ev.timestamp),
  };
}

function parcelColumns(): ColumnDef<DashboardParcel>[] {
  return [
    {
      accessorKey: 'account_number',
      header: 'Account #',
      cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)' }}>{getValue() as string}</span>,
    },
    { accessorKey: 'name', header: 'Name', cell: ({ row }) => row.original.name || row.original.owner_name || '—' },
    { accessorKey: 'client_name', header: 'Client', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'current_market_value', header: 'Current', cell: ({ getValue }) => money(getValue() as number | null) },
    { accessorKey: 'proposed_market_value', header: 'Proposed', cell: ({ getValue }) => money(getValue() as number | null) },
    {
      id: 'state',
      header: 'Status',
      cell: ({ row }) => {
        const p = row.original;
        if (p.under_appeal) return <Badge variant="warning" dot>Under appeal</Badge>;
        if (p.settled) return <Badge variant="success" dot>Settled</Badge>;
        return <Badge variant="neutral">{p.status.replace(/_/g, ' ')}</Badge>;
      },
    },
  ];
}

/**
 * TwoColumn — main content + a 360px side rail on desktop; stacks to a single
 * column below the `lg` breakpoint so the side rail doesn't crush the table on
 * a phone (closes the grid gap deferred from the responsive-shell PR).
 */
function TwoColumn({ compact, children }: { compact: boolean; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: compact ? '1fr' : 'minmax(0, 1fr) 360px',
      gap: 'var(--sp-5)',
      alignItems: 'start',
    }}>
      {children}
    </div>
  );
}

/**
 * ChartRow — two equal chart cards side-by-side on desktop; stacks to a single
 * column below the `lg` breakpoint so donuts stay legible on a phone.
 */
function ChartRow({ compact, children }: { compact: boolean; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
      gap: 'var(--sp-5)',
      alignItems: 'stretch',
      marginBottom: 'var(--sp-7)',
    }}>
      {children}
    </div>
  );
}

function PageHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <header style={{ marginBottom: 'var(--sp-6)' }}>
      <h1 style={{
        margin: 0,
        fontSize: 'var(--fs-3xl)',
        fontWeight: 'var(--fw-semibold)',
        letterSpacing: 'var(--ls-tight)',
        color: 'var(--color-ink)',
      }}>{title}</h1>
      <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
        {subtitle}
      </p>
    </header>
  );
}

/**
 * DashboardNotifications — the top 5 recent notifications for the current user,
 * on the dashboard (resolves TODO(#41)). Reads the same self-scoped feed as the
 * header bell + Notifications page (lib/notifications). Renders nothing when the
 * feed is empty so it never leaves a bare header on a fresh account. Clicking a
 * row deep-links to the entity (marking it read); "View all" opens the page.
 */
function DashboardNotifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery<AppNotification[]>({
    queryKey: ['notifications-dashboard'],
    queryFn: ({ signal }) => api.get<AppNotification[]>('/api/my-notifications?days=30&limit=5', signal),
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-bell'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    },
  });

  const all = query.data ?? [];
  const top = useMemo(
    () => [...all].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')).slice(0, 5),
    [all],
  );
  const unread = all.filter((n) => isUnread(n)).length;

  // Don't clutter a fresh dashboard with an empty section.
  if (!query.isLoading && top.length === 0) return null;

  function openRow(n: AppNotification) {
    if (isUnread(n)) markRead.mutate(n.id);
    const route = routeFor(n);
    navigate(route ?? '/notifications');
  }

  return (
    <section style={{ marginBottom: 'var(--sp-7)' }}>
      <SectionHeader
        title="Notifications"
        caption={unread > 0 ? `${unread} unread` : 'recent'}
      />
      <div style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--sh-sm)',
        overflow: 'hidden',
      }}>
        {query.isLoading ? (
          <div style={{ padding: 'var(--sp-4)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {top.map((n) => {
              const meta = metaFor(n.trigger_type);
              const unreadRow = isUnread(n);
              const urgency = (n.urgency ?? 'normal') as string;
              return (
                <li
                  key={n.id}
                  onClick={() => openRow(n)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-3)',
                    padding: 'var(--sp-3) var(--sp-4)',
                    borderBottom: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    background: unreadRow ? 'var(--brand-primary-bg)' : undefined,
                  }}
                >
                  <span aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 24, color: 'var(--color-ink-3)' }}><NotificationTypeIcon type={n.trigger_type} size={16} /></span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: 'block',
                      fontSize: 'var(--fs-md)',
                      fontWeight: unreadRow ? 'var(--fw-semibold)' : 'var(--fw-regular, 400)',
                      color: 'var(--color-ink)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>{n.title || meta.label}</span>
                    <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
                      {meta.label} · {relativeTime(n.created_at)}
                    </span>
                  </span>
                  <Badge variant={urgencyVariant[urgency] ?? 'neutral'}>
                    {urgencyLabel[urgency] ?? urgency}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
        <button
          type="button"
          onClick={() => navigate('/notifications')}
          style={{
            width: '100%',
            padding: 'var(--sp-3)',
            border: 'none',
            background: 'var(--color-card)',
            color: 'var(--brand-primary)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-medium)',
            fontFamily: 'inherit',
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          View all notifications
        </button>
      </div>
    </section>
  );
}

function SectionHeader({ title, caption }: { title: string; caption?: string }) {
  return (
    <header style={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      marginBottom: 'var(--sp-3)',
    }}>
      <h2 style={{ margin: 0, fontSize: 'var(--fs-xl)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{title}</h2>
      {caption && <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>{caption}</span>}
    </header>
  );
}

function EmptyView({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}

function ErrorView({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-5)',
      background: 'var(--color-status-red-bg)',
      border: '1px solid var(--color-status-red)',
      color: 'var(--color-status-red)',
      borderRadius: 'var(--r-lg)',
    }}>
      <strong>Failed to load dashboard:</strong> {message}
    </div>
  );
}

/** My open tasks (ATA part 3 §8): the five most pressing assigned items, each a deep link; nothing when there are none. */
function DashboardTasks() {
  const navigate = useNavigate();
  const query = useQuery<{ rows: Array<{ id: string; title: string; kind_label: string; due_date: string | null; action_url: string | null }> }>({
    queryKey: ['tasks-dashboard'],
    queryFn: ({ signal }) => api.get('/api/tasks?status=open', signal),
    refetchInterval: 60_000,
  });
  const rows = (query.data?.rows ?? []).slice(0, 5);
  if (!rows.length) return null;
  return (
    <section style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', marginBottom: 'var(--sp-5)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-2)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>My open tasks</h2>
        <button type="button" onClick={() => navigate('/tasks')} style={{ border: 'none', background: 'transparent', color: 'var(--brand-primary)', cursor: 'pointer', font: 'inherit', fontSize: 'var(--fs-sm)' }}>View all</button>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--sp-1)' }}>
        {rows.map((t) => (
          <li key={t.id}>
            <button type="button" onClick={() => navigate(t.action_url || `/tasks?task=${t.id}`)} style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: 'var(--sp-1) 0', cursor: 'pointer', font: 'inherit', display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
              <span style={{ color: 'var(--color-ink)' }}>{t.title}</span>
              <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', whiteSpace: 'nowrap' }}>{t.due_date ? `Due ${t.due_date}` : t.kind_label}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
